import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { cx } from "../components/wf";
import { api, num, weekStart } from "../lib/api";

/** Project items due in the next 3 days that the signed-in user leads or helps on. */
export function DueSoon() {
  const q = useQuery({ queryKey: ["due-soon"], queryFn: () => api("/me/due-soon?days=3") });
  const items = q.data ?? [];
  return (
    <div className="rounded-2xl border border-wf-line bg-wf-s2 p-5" data-testid="due-soon">
      <p className="mb-3 text-[13px] font-bold uppercase tracking-[0.08em] text-wf-mute">Due Soon</p>
      {items.length === 0 ? <p className="text-sm text-wf-mute">Nothing due in the next 3 days.</p> : (
        <div className="flex flex-col gap-2">
          {items.slice(0, 5).map((d) => (
            <Link key={d.task_id} to={`/projects/${d.project_id}`} data-testid={`due-soon-${d.task_id}`} className={cx("flex items-center gap-3 rounded-xl border bg-wf-surface px-3 py-2.5 hover:bg-wf-soft", d.overdue || d.sent_back ? "border-wf-err" : "border-transparent")}>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-bold">
                  {d.sent_back && <span data-testid={`due-soon-sent-back-${d.task_id}`} className="rounded bg-wf-err px-1.5 py-0.5 text-[10px] font-extrabold text-white">SENT BACK</span>}
                  {d.title}
                </p>
                <p className="truncate text-xs text-wf-mute">{d.customer_name} · {d.role} · {d.priority}</p>
                {!!d.sent_back_reason && <p className="line-clamp-2 text-xs font-semibold text-wf-err">Fix: {d.sent_back_reason}</p>}
              </div>
              {!!d.due_date && <span className={cx("text-xs font-bold", d.overdue ? "text-wf-err" : "text-wf-ink2")}>{d.overdue ? "Overdue · " : ""}{d.due_date}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** The signed-in user's hours for one week. */
export function WeekRecap() {
  const [offset, setOffset] = useState(0);
  const start = weekStart(offset);
  const q = useQuery({ queryKey: ["my-hours", start], queryFn: () => api(`/me/hours?start=${start}`) });
  const d = q.data;
  return (
    <div className="rounded-2xl border border-wf-line bg-wf-s2 p-5" data-testid="week-recap">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[13px] font-bold uppercase tracking-[0.08em] text-wf-mute">My Hours</p>
        <div className="flex items-center gap-1">
          <button data-testid="week-prev" onClick={() => setOffset((o) => o - 1)} className="rounded-full p-1 text-wf-brand hover:bg-wf-soft"><ChevronLeft size={18} /></button>
          <span className="text-xs font-semibold">{d ? `${d.start} – ${d.end}` : start}</span>
          <button data-testid="week-next" disabled={offset >= 0} onClick={() => setOffset((o) => o + 1)} className="rounded-full p-1 text-wf-brand hover:bg-wf-soft disabled:opacity-30"><ChevronRight size={18} /></button>
        </div>
      </div>
      <p className="text-3xl font-extrabold" data-testid="week-total-hours">{num(d?.total_hours)} <span className="text-base font-semibold text-wf-mute">hrs</span></p>
      <div className="mt-3 grid grid-cols-7 gap-1">
        {(d?.days ?? []).map((day) => (
          <div key={day.date} title={day.entries.map((e) => `${e.customer_name}: ${e.hours}h`).join("\n")} className={cx("rounded-lg py-2 text-center", day.hours ? "bg-wf-soft text-wf-softink" : "bg-wf-surface text-wf-mute")}>
            <p className="text-[10px] font-semibold">{new Date(day.date + "T00:00").toLocaleDateString("en-US", { weekday: "short" })}</p>
            <p className="text-sm font-bold">{num(day.hours)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
